import { supabase, db } from '@/lib/supabase'
import { appFetch, appFetchPublic, isAppApiConfigured } from '@/lib/appApi'
import { escapeIlikePattern, buildOrIlikeClause } from '@/lib/dashboard-v2/postgrestOrIlike'
import { DASHBOARD_EXPORT_MAX_ROWS, type DashboardExportResult } from '@/lib/dashboard-v2/dashboardExportLimits'
import { guardStockForNormalOnly } from '@/lib/product-type'
import { stripInternalProductShippingFields } from '@/lib/pricing/resolveShippingCosts'
import {
  canonicalVariantAttributeKey,
  deriveProductOptionsFromVariantRows,
  normalizeVariantAttributesForWrite,
} from '@/lib/variantAttributesNormalize'

/** Normalize attributes object so key order is deterministic (for DB unique index & variant_lookup keys). */
export function normalizeAttributes(attrs: Record<string, string>): Record<string, string> {
  if (!attrs || typeof attrs !== 'object') return {}
  return Object.fromEntries(
    Object.entries(attrs).sort(([a], [b]) => a.localeCompare(b))
  )
}

/** Build variant_lookup key from normalized attributes: "Color:Red|Size:M" */
export function variantLookupKey(attrs: Record<string, string>): string {
  const n = normalizeAttributes(attrs)
  return Object.entries(n)
    .map(([k, v]) => `${k}:${v}`)
    .join('|')
}

/** PDP / variant_lookup: ignore DB-only disambiguation keys (leading `_`). */
function stripInternalAttributeKeys(attrs: Record<string, string>): Record<string, string> {
  return normalizeAttributes(
    Object.fromEntries(Object.entries(attrs || {}).filter(([k]) => !k.startsWith('_'))),
  )
}

/** Inverse of variantLookupKey for PDP default selection (same normalized key order). */
export function parseVariantLookupKey(key: string): Record<string, string> {
  const raw: Record<string, string> = {}
  for (const segment of String(key).split('|')) {
    const idx = segment.indexOf(':')
    if (idx <= 0) continue
    const k = segment.slice(0, idx).trim()
    const v = segment.slice(idx + 1).trim()
    if (k && v) raw[k] = v
  }
  return normalizeAttributes(raw)
}

/**
 * When AliExpress (or other sources) send display labels like `14:193#black` but no structured
 * `attributes`, infer `{ color }` from the substring after `#` so DB triggers populate
 * variant_title / option_count and storefront option keys exist.
 */
const AE_MATRIX_ATTR_MAX = 240

function looksLikeAeMatrixFingerprint(s: string): boolean {
  const t = String(s ?? '').trim()
  if (!t) return false
  return t.includes('#') || t.includes(';') || /^\d+:\d+/.test(t)
}

function buildAeMatrixFingerprint(external?: string | null, sku?: string | null): string {
  const candidates = [external, sku].map((x) => String(x ?? '').trim()).filter(Boolean)
  const src = candidates.find((t) => looksLikeAeMatrixFingerprint(t))
  if (!src) return ''
  const norm = src.replace(/\s+/g, ' ').trim()
  if (!norm) return ''
  return norm.length > AE_MATRIX_ATTR_MAX ? norm.slice(0, AE_MATRIX_ATTR_MAX) : norm
}

function withAeMatrixDisambiguation(
  attrs: Record<string, string>,
  v: { external_sku_id?: string | null; sku?: string | null },
): Record<string, string> {
  const fp = buildAeMatrixFingerprint(v.external_sku_id ?? null, v.sku ?? null)
  if (!fp) return attrs
  return normalizeAttributes({ ...attrs, __ae_matrix: fp })
}

export function resolveVariantAttributesForPersist(v: {
  attributes?: Record<string, string> | null
  name?: string | null
  sku?: string | null
  external_sku_id?: string | null
}): Record<string, string> {
  const finish = (attrs: Record<string, string>) =>
    normalizeVariantAttributesForWrite(withAeMatrixDisambiguation(attrs, v))

  const raw = v.attributes
  const base = normalizeAttributes(
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, string>) : {},
  )

  const colorFromHash = (s: string) => {
    const t = String(s ?? '').trim()
    const i = t.lastIndexOf('#')
    if (i < 0 || i >= t.length - 1) return ''
    return t.slice(i + 1).trim()
  }

  /** DS fallback uses `{ Option: "14:193#black" }` when ae_sku_property_dtos are missing — treat as unlabeled SKU, infer color. */
  const keys = Object.keys(base)
  if (keys.length === 1 && keys[0].toLowerCase() === 'option') {
    const only = String(base[keys[0]] ?? '').trim()
    const c = colorFromHash(only) || colorFromHash(v.name ?? '') || colorFromHash(v.sku ?? '')
    if (c) return finish(normalizeAttributes({ color: c }))
  }

  if (Object.keys(base).length > 0) return finish(base)

  const color = colorFromHash(v.name ?? '') || colorFromHash(v.sku ?? '')
  if (!color) return finish(base)
  return finish(normalizeAttributes({ color }))
}

function resolveVariantImageUrl(v: any): string | null {
  const u = v?.image_url ?? v?.image
  if (u == null) return null
  const s = String(u).trim()
  return s || null
}

/** Variable products must keep ≥1 public axis after normalization (not internal-only). */
function assertVariantHasPublicAttributes(normalized: Record<string, string>, context: string): void {
  const pub = Object.keys(normalized).filter((k) => !k.startsWith('_'))
  if (pub.length === 0) {
    throw new Error(
      `${context}: variant must have at least one public attribute (e.g. Color / Size) after normalization — got only internal keys.`,
    )
  }
}

/** Product list / cards: variable when we have variant rows (embedded list or aggregated count). */
export function productHasVariantRows(product: Pick<Product, 'has_variants' | 'variant_count'> & { variants?: unknown[] }): boolean {
  if (Array.isArray(product.variants) && product.variants.length > 0) return true
  return (product.variant_count ?? 0) > 0
}

/** Persist stock / inventory_quantity: explicit null = untracked (dropshipping). */
function coerceVariantStockFields(v: any): { stock: number | null; inventory_quantity: number | null } {
  const stock =
    v.stock === null ? null : v.stock === undefined ? 0 : Number(v.stock)
  const inventory_quantity = (() => {
    if (v.inventory_quantity !== undefined && v.inventory_quantity !== null) return v.inventory_quantity
    if (v.stock === null) return null
    if (v.stock === undefined) return 0
    return Math.floor(Number(v.stock))
  })()
  return { stock, inventory_quantity }
}

/** Normalize slug: lowercase, single dashes, alphanumeric + dash only */
function normalizeSlugBase(s: string | null | undefined): string {
  const base = (s || 'product').toString().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '')
  return base || 'product'
}

/** Ensure slug is unique for this user (avoids products_user_slug_unique violation). */
async function ensureUniqueProductSlug(
  userId: string,
  baseSlug: string | null | undefined,
  excludeProductId?: string
): Promise<string> {
  const base = normalizeSlugBase(baseSlug)
  const { data: rows } = await db.from('products').select('id, slug').eq('user_id', userId).not('slug', 'is', null)
  const used = new Set<string>()
  for (const r of rows || []) {
    const slug = (r as { slug: string }).slug
    if (slug) used.add(slug.toLowerCase())
  }
  if (excludeProductId) {
    const current = (rows || []).find((r: any) => r.id === excludeProductId) as { slug: string } | undefined
    if (current?.slug) used.delete(current.slug.toLowerCase())
  }
  let candidate = base
  let n = 1
  while (used.has(candidate)) {
    candidate = `${base}-${n}`
    n += 1
  }
  return candidate
}

// Option tables (normalized, Shopify-style)
export interface ProductOption {
  id: string
  product_id: string
  name: string
  position: number
  created_at?: string
  updated_at?: string
  values?: ProductOptionValue[]
}

export interface ProductOptionValue {
  id: string
  option_id: string
  value: string
  position: number
  created_at?: string
  updated_at?: string
}

/** Replace product_options + product_option_values from derived axes (variant.attributes is source of truth). */
async function replaceProductOptionsFromDerived(
  productId: string,
  derived: { name: string; values: string[] }[],
): Promise<void> {
  const { data: existingOpts } = await db.from('product_options').select('id').eq('product_id', productId)
  if (existingOpts?.length) {
    await db.from('product_option_values').delete().in('option_id', existingOpts.map((o: any) => o.id))
    await db.from('product_options').delete().eq('product_id', productId)
  }
  const slice = derived.slice(0, 3)
  for (let i = 0; i < slice.length; i++) {
    const opt = slice[i]
    const { data: optRow, error: optErr } = await db
      .from('product_options')
      .insert({ product_id: productId, name: opt.name, position: i })
      .select('id')
      .single()
    if (optErr) throw optErr
    if (optRow && opt.values.length > 0) {
      await db.from('product_option_values').insert(
        opt.values.map((value, j) => ({ option_id: optRow.id, value, position: j })),
      )
    }
  }
}

/** Admin getById: options list matches derived variant axes; reuse DB ids when name/value align. */
function detailOptionsMergedFromVariants(
  productId: string,
  variants: Array<{ attributes?: Record<string, string> | null }>,
  dbOptions: any[],
): ProductOption[] {
  const derived = deriveProductOptionsFromVariantRows(variants)
  const sortedDb = [...(dbOptions || [])].sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
  return derived.map((d, i) => {
    const dbOpt =
      sortedDb.find(
        (o: any) =>
          canonicalVariantAttributeKey(String(o.name ?? '')).toLowerCase() === d.name.toLowerCase(),
      ) ?? sortedDb[i]
    const vals = (dbOpt?.values || []) as ProductOptionValue[]
    const optId = (dbOpt?.id as string) || `local-opt-${productId}-${i}`
    return {
      id: optId,
      product_id: productId,
      name: d.name,
      position: i,
      created_at: dbOpt?.created_at,
      updated_at: dbOpt?.updated_at,
      values: d.values.map((value, j) => {
        const hit = vals.find((x: any) => String(x.value) === value)
        return {
          id: (hit?.id as string) || `local-ov-${productId}-${i}-${j}`,
          option_id: optId,
          value,
          position: j,
          created_at: hit?.created_at,
          updated_at: hit?.updated_at,
        } as ProductOptionValue
      }),
    }
  })
}

export interface VariantImage {
  id: string
  variant_id: string
  url: string
  position: number
  is_primary: boolean
  created_at?: string
  updated_at?: string
}

export interface ProductImage {
  id: string
  product_id: string
  url: string
  position: number
  created_at?: string
  updated_at?: string
}

// Types
export interface ProductVariant {
  id: string
  product_id: string
  user_id?: string
  store_id?: string
  name?: string
  sku: string | null
  price: number | null
  /** null = not tracked (e.g. dropshipping) */
  stock?: number | null
  image_url?: string | null
  position: number
  status: 'active' | 'inactive' | 'out_of_stock' | 'archived'
  deleted_at: string | null
  /** Combination map e.g. { Color: "Red", Size: "M" } — always normalized before save */
  attributes?: Record<string, string>
  /**
   * Teinte d’affichage fiable (#RRGGBB), optionnelle — prioritaire sur le libellé pour la pastille.
   * Peut être renseigné par import / éditeur sans changer la valeur d’option affichée.
   */
  display_hex?: string | null
  barcode?: string | null
  compare_at_price?: number | null
  weight?: number | null
  cost_price?: number | null
  is_default?: boolean
  inventory_policy?: string
  inventory_quantity?: number | null
  inventory_reserved?: number
  created_at?: string
  updated_at?: string
  images?: VariantImage[]
  /** AliExpress / supplier COGS in supplier currency (import) */
  supplier_unit_price?: number | null
  supplier_currency?: string | null
}

/** Product type / category (DB: products.product_type) */
export type ProductType =
  | 'simple'
  | 'variable'
  | 'digital'
  | 'service'
  | 'electronics'
  | 'cosmetics'
  | 'fashion'
  | 'home'
  | 'sports'
  | 'other'

/** Labels for product type dropdown (FR) */
export const PRODUCT_TYPE_OPTIONS: { value: ProductType; label: string }[] = [
  { value: 'simple', label: 'Simple' },
  { value: 'variable', label: 'Variable (avec variantes)' },
  { value: 'digital', label: 'Digital' },
  { value: 'service', label: 'Service' },
  { value: 'electronics', label: 'Électronique' },
  { value: 'cosmetics', label: 'Cosmétique' },
  { value: 'fashion', label: 'Mode / Fashion' },
  { value: 'home', label: 'Maison' },
  { value: 'sports', label: 'Sport' },
  { value: 'other', label: 'Autre' },
]

const STOREFRONT_VISIBLE_PRODUCT_FILTER =
    'status.eq.active,' +
    'and(is_dropshipping.eq.true,status.neq.draft),' +
    'and(product_type.eq.dropshipping,status.neq.draft),' +
    'and(import_source.eq.aliexpress,status.neq.draft),' +
    'and(supplier_url.not.is.null,status.neq.draft)'

function isDropshippingProduct(product: {
    is_dropshipping?: boolean | null
    product_type?: string | null
    import_source?: string | null
    supplier_url?: string | null
}): boolean {
    return (
        product.is_dropshipping === true ||
        String(product.product_type ?? '').toLowerCase() === 'dropshipping' ||
        String(product.import_source ?? '').toLowerCase() === 'aliexpress' ||
        (typeof product.supplier_url === 'string' && product.supplier_url.trim() !== '')
    )
}

export interface Product {
  id: string
  user_id: string
  store_id: string
  name: string
  slug: string | null
  price: number
  stock: number
  status: 'active' | 'out_of_stock' | 'draft'
  image: string | null
  images: string[]
  orders_count: number
  meta_title: string | null
  meta_description: string | null
  created_at: string
  updated_at: string
  has_variants: boolean
  variants?: ProductVariant[]
  /** List view: aggregated from variants when has_variants (one row per product) */
  variant_count?: number
  /**
   * Storefront grid: first variants for swatch row (from getByStoreId).
   * Swatch fill HEX is derived at render time via `normalizeColorCached` from the color axis value
   * (original label stays in `attributes`; optional `display_hex` is never required from the API).
   */
  variant_swatches?: Array<{
    id: string
    attributes?: Record<string, string>
    image_url?: string | null
    display_hex?: string | null
  }>
  min_price?: number
  max_price?: number
  total_stock?: number
  warehouse_id: string | null
  category_id: string | null
  product_type?: ProductType
  barcode?: string | null
  track_inventory?: boolean
  /** Unit COGS in TND — populated for dropshipping and buy pricing; used in order snapshots */
  purchase_price?: number | null
  /** Supplier product page URL */
  supplier_url?: string | null
  /** Unit cost from supplier in `supplier_currency` */
  supplier_price?: number | null
  supplier_currency?: string | null
  is_dropshipping?: boolean
  /** Computed column: 'dropshipping' | 'normal' */
  business_model?: 'dropshipping' | 'normal'
  /** Default variant ID — always set after product creation */
  default_variant_id?: string | null
  /** Supplier-side stock when known; null = unknown / not synced */
  external_stock?: number | null
  /** Markup % over cost (TND) at import or last pricing update */
  dropship_margin_percent?: number | null
  /** Auto pricing multiplier over supplier_price (supplier currency) */
  dropship_margin_multiplier?: number | null
  /** Auto pricing fixed fee (supplier currency) */
  dropship_fixed_fee?: number | null
  /** e.g. aliexpress when imported via official API */
  import_source?: string | null
  cover_image?: string | null
  digital_file_url?: string | null
  service_duration_minutes?: number | null
  service_schedule?: Record<string, unknown> | null
  options?: ProductOption[]
  product_images?: ProductImage[]
  /** Original price before discount — shown as strikethrough when > price */
  compare_at_price?: number | null
  /** Frais livraison facturés au client (TND) — visible storefront pour total commande */
  shipping_fee?: number | null
  /** Coût livraison interne (TND) — dashboard uniquement, jamais storefront */
  delivery_cost?: number | null
  /** Coût retour interne (TND) — dashboard uniquement */
  return_cost?: number | null
  /**
   * Optional PDP / quick-view size chart (demo + future API).
   * Not persisted by all backends — safe optional.
   */
  size_chart?: {
    type: 'clothing' | 'shoes'
    values: Record<string, string>[]
  } | null
}

export type ProductInsert = Omit<Product, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'orders_count' | 'variants' | 'options' | 'product_images'>
export type ProductUpdate = Partial<Omit<ProductInsert, 'store_id'>>

/** Storefront payload: product + default_variant + options + variants + variant_lookup */
export interface ProductStorefrontPayload {
  product: Product & { images?: { url: string; position: number }[] }
  default_variant: string | null
  options: { name: string; values: string[] }[]
  variants: ProductVariant[]
  variant_lookup: Record<string, string>
}

const MAX_MATCHING_IDS = 10_000

export type ProductListPageStatus = 'all' | 'active' | 'out_of_stock' | 'draft'

export type ProductListPageResult = { rows: Product[]; totalCount: number }

/** Dashboard list: merge active variant aggregates into product rows (same rules as getAll). */
async function mergeDashboardProductVariantAggregates(list: Product[]): Promise<Product[]> {
    if (list.length === 0) return []
    const productIds = list.map((p) => p.id)
    const { data: variants } = await db
        .from('product_variants')
        .select('product_id, price, inventory_quantity')
        .in('product_id', productIds)
        .eq('status', 'active')

    const agg: Record<string, { min_price: number; max_price: number; count: number; total_stock: number }> = {}
    for (const v of variants || []) {
        const pid = (v as { product_id: string }).product_id
        if (!agg[pid]) agg[pid] = { min_price: Infinity, max_price: -Infinity, count: 0, total_stock: 0 }
        const p = (v as { price: number | null; inventory_quantity: number | null }).price
        const q = (v as { price: number | null; inventory_quantity: number | null }).inventory_quantity ?? 0
        if (p != null) {
            agg[pid].min_price = Math.min(agg[pid].min_price, p)
            agg[pid].max_price = Math.max(agg[pid].max_price, p)
        }
        agg[pid].count += 1
        agg[pid].total_stock += Number(q) || 0
    }

    return list.map((p: Product & { id: string }) => {
        const a = agg[p.id]
        if (!p.has_variants || !a) return p
        const isDs = isDropshippingProduct(p)
        const derivedStatus = (a.total_stock === 0 && !isDs) ? 'out_of_stock' : p.status
        return {
            ...p,
            is_dropshipping: isDs,
            variant_count: a.count,
            min_price: a.min_price === Infinity ? undefined : a.min_price,
            max_price: a.max_price === -Infinity ? undefined : a.max_price,
            total_stock: a.total_stock,
            price: a.min_price === Infinity ? p.price : a.min_price,
            stock: a.total_stock,
            status: derivedStatus,
        }
    })
}

// Products Service
export const productsService = {
    // Get all products for a specific store (dashboard). One row per product; variant data aggregated.
    async getAll(storeId: string): Promise<Product[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; rows: Product[] }>(
                `/api/app/products/all?storeId=${encodeURIComponent(storeId)}`
            )
            if (!j || typeof j !== 'object' || !('rows' in j) || !Array.isArray((j as { rows: unknown }).rows)) {
                throw new Error('Invalid API response')
            }
            return (j as { rows: Product[] }).rows
        }

        const { data: products, error } = await db
            .from('products')
            .select('*')
            .eq('store_id', storeId)
            .order('created_at', { ascending: false })

        if (error) throw error
        const list = products || []
        if (list.length === 0) return []

        return mergeDashboardProductVariantAggregates(list as Product[])
    },

    /**
     * Dashboard: server pagination + total count (Supabase range + count).
     * Sorting uses base `products` columns (variant-derived price/stock still merged per row).
     */
    async listPage(
        storeId: string,
        opts: {
            page: number
            pageSize: number
            q: string
            sortKey: string | null
            sortDir: 'asc' | 'desc'
            status: ProductListPageStatus
        }
    ): Promise<ProductListPageResult> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const page = Math.max(1, Math.floor(opts.page))
            const pageSize = Math.min(20, Math.max(1, Math.floor(opts.pageSize)))
            const params = new URLSearchParams({
                storeId,
                page: String(page),
                pageSize: String(pageSize),
                q: opts.q ?? '',
                sortKey: opts.sortKey ?? '',
                sortDir: opts.sortDir,
                status: opts.status,
            })
            const j = await appFetch<{ ok: true; rows: Product[]; totalCount: number }>(
                `/api/app/products/page?${params.toString()}`
            )
            if (
                !j ||
                typeof j !== 'object' ||
                !Array.isArray((j as { rows?: unknown }).rows) ||
                typeof (j as { totalCount?: unknown }).totalCount !== 'number'
            ) {
                throw new Error('Invalid API response')
            }
            return { rows: (j as { rows: Product[] }).rows, totalCount: (j as { totalCount: number }).totalCount }
        }

        const page = Math.max(1, Math.floor(opts.page))
        const pageSize = Math.min(20, Math.max(1, Math.floor(opts.pageSize)))
        const from = (page - 1) * pageSize
        const to = from + pageSize - 1

        let query = db.from('products').select('*', { count: 'exact' }).eq('store_id', storeId)

        if (opts.status === 'active') query = query.eq('status', 'active')
        else if (opts.status === 'draft') query = query.eq('status', 'draft')
        else if (opts.status === 'out_of_stock') {
            query = query
                .eq('status', 'out_of_stock')
                .neq('is_dropshipping', true)
        }

        const q = (opts.q || '').trim()
        if (q) {
            const p = `%${escapeIlikePattern(q)}%`
            query = query.ilike('name', p)
        }

        const sortKey = opts.sortKey || ''
        const asc = opts.sortDir === 'asc'
        if (sortKey === 'product') query = query.order('name', { ascending: asc })
        else if (sortKey === 'price') query = query.order('price', { ascending: asc })
        else if (sortKey === 'stock') query = query.order('stock', { ascending: asc })
        else if (sortKey === 'sales') query = query.order('orders_count', { ascending: asc })
        else query = query.order('created_at', { ascending: false })

        const { data: products, error, count } = await query.range(from, to)
        if (error) throw error
        const list = (products || []) as Product[]
        const rows = await mergeDashboardProductVariantAggregates(list)
        return { rows, totalCount: count ?? 0 }
    },

    /** IDs matching filters (cap 10k) for “select all results”. */
    async listMatchingIds(
        storeId: string,
        opts: { q: string; status: ProductListPageStatus }
    ): Promise<string[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({
                storeId,
                q: opts.q ?? '',
                status: opts.status,
            })
            const j = await appFetch<{ ok: true; ids: string[] }>(
                `/api/app/products/matching-ids?${params.toString()}`
            )
            if (!j || typeof j !== 'object' || !Array.isArray((j as { ids?: unknown }).ids)) {
                throw new Error('Invalid API response')
            }
            return (j as { ids: string[] }).ids
        }

        let query = db.from('products').select('id').eq('store_id', storeId).limit(MAX_MATCHING_IDS)

        if (opts.status === 'active') query = query.eq('status', 'active')
        else if (opts.status === 'draft') query = query.eq('status', 'draft')
        else if (opts.status === 'out_of_stock') {
            query = query
                .eq('status', 'out_of_stock')
                .neq('is_dropshipping', true)
        }

        const q = (opts.q || '').trim()
        if (q) {
            const p = `%${escapeIlikePattern(q)}%`
            query = query.ilike('name', p)
        }

        const { data, error } = await query.order('created_at', { ascending: false })
        if (error) throw error
        return (data || []).map((r: { id: string }) => r.id)
    },

    /** Tab counts for dashboard filters (cheap head counts). */
    async countByStatusTabs(storeId: string): Promise<{
        all: number
        active: number
        draft: number
        out_of_stock: number
    }> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{
                ok: true
                counts: { all: number; active: number; draft: number; out_of_stock: number }
            }>(`/api/app/products/tab-counts?storeId=${encodeURIComponent(storeId)}`)
            if (!j || typeof j !== 'object' || !('counts' in j) || typeof (j as { counts?: unknown }).counts !== 'object') {
                throw new Error('Invalid API response')
            }
            const c = (j as { counts: { all: number; active: number; draft: number; out_of_stock: number } }).counts
            return {
                all: c.all,
                active: c.active,
                draft: c.draft,
                out_of_stock: c.out_of_stock,
            }
        }

        const base = () => db.from('products').select('*', { count: 'exact', head: true }).eq('store_id', storeId)
        const [all, active, draft, oos] = await Promise.all([
            base(),
            base().eq('status', 'active'),
            base().eq('status', 'draft'),
            base()
                .eq('status', 'out_of_stock')
                .neq('is_dropshipping', true)
        ])
        if (all.error) throw all.error
        if (active.error) throw active.error
        if (draft.error) throw draft.error
        if (oos.error) throw oos.error
        return {
            all: all.count ?? 0,
            active: active.count ?? 0,
            draft: draft.count ?? 0,
            out_of_stock: oos.count ?? 0,
        }
    },

    /** Export helper: fetch all rows matching filters in batches (dashboard fields). */
    async fetchAllMatchingForExport(
        storeId: string,
        opts: { q: string; status: ProductListPageStatus }
    ): Promise<DashboardExportResult<Product>> {
        const out: Product[] = []
        const batch = 500
        let page = 1
        while (true) {
            const { rows, totalCount } = await productsService.listPage(storeId, {
                page,
                pageSize: batch,
                q: opts.q,
                sortKey: null,
                sortDir: 'desc',
                status: opts.status,
            })
            out.push(...rows)
            if (out.length >= DASHBOARD_EXPORT_MAX_ROWS) {
                return { rows: out.slice(0, DASHBOARD_EXPORT_MAX_ROWS), truncated: true }
            }
            if (out.length >= totalCount || rows.length === 0) break
            page += 1
            if (page > 200) break
        }
        return { rows: out, truncated: false }
    },

    // Get all products for a specific store (public storefront). One row per product; variant data aggregated.
    async getByStoreId(storeId: string, opts?: { categoryId?: string; limit?: number }): Promise<Product[]> {
        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId })
            if (opts?.categoryId) params.set('categoryId', opts.categoryId)
            if (opts?.limit != null && opts.limit > 0) params.set('limit', String(opts.limit))
            const j = await appFetchPublic<{ ok: true; rows: Product[] }>(
                `/api/app/public/catalog/products?${params.toString()}`
            )
            if (!j || typeof j !== 'object' || !Array.isArray((j as { rows?: unknown }).rows)) {
                throw new Error('Invalid API response')
            }
            return (j as { rows: Product[] }).rows
        }
        // Dropshipping products don't track local stock, so they must appear even when
        // status = 'out_of_stock' (which the importer set because stock=0 at import time).
        // Show: all active products  +  all non-draft dropshipping products.
        let query = db
            .from('products')
            .select('*, category:categories(id, slug, name)')
            .eq('store_id', storeId)
            .or(STOREFRONT_VISIBLE_PRODUCT_FILTER)
            .order('created_at', { ascending: false })

        if (opts?.categoryId) {
            query = query.eq('category_id', opts.categoryId)
        }
        if (opts?.limit != null && opts.limit > 0) {
            query = query.limit(opts.limit)
        }

        const { data: products, error } = await query
        if (error) throw error
        const list = products || []
        if (list.length === 0) return []

        const productIds = list.map((p: { id: string }) => p.id)
        const { data: variants } = await db
            .from('product_variants')
            .select('product_id, price, inventory_quantity')
            .in('product_id', productIds)
            .eq('status', 'active')

        const agg: Record<string, { min_price: number; max_price: number; count: number; total_stock: number }> = {}
        for (const v of variants || []) {
            const pid = (v as { product_id: string }).product_id
            if (!agg[pid]) agg[pid] = { min_price: Infinity, max_price: -Infinity, count: 0, total_stock: 0 }
            const p = (v as { price: number | null; inventory_quantity: number | null }).price
            const q = (v as { price: number | null; inventory_quantity: number | null }).inventory_quantity ?? 0
            if (p != null) {
                agg[pid].min_price = Math.min(agg[pid].min_price, p)
                agg[pid].max_price = Math.max(agg[pid].max_price, p)
            }
            agg[pid].count += 1
            agg[pid].total_stock += Number(q) || 0
        }

        const variantIds = list.filter((p: Product) => p.has_variants).map((p: { id: string }) => p.id)
        let swatchesByProduct: Record<string, NonNullable<Product['variant_swatches']>> = {}
        if (variantIds.length > 0) {
            const { data: swRows } = await db
                .from('product_variants')
                .select('id, product_id, attributes, image_url, position')
                .in('product_id', variantIds)
                .eq('status', 'active')
                .order('position', { ascending: true })
            for (const row of swRows || []) {
                const pid = (row as { product_id: string }).product_id
                if (!swatchesByProduct[pid]) swatchesByProduct[pid] = []
                if (swatchesByProduct[pid].length >= 8) continue
                swatchesByProduct[pid].push({
                    id: String((row as { id: string }).id),
                    attributes: ((row as { attributes?: Record<string, string> }).attributes || {}) as Record<string, string>,
                    image_url: (row as { image_url?: string | null }).image_url ?? null,
                })
            }
        }

        const { data: galleryRows, error: galleryErr } = await db
            .from('product_images')
            .select('product_id, url, position')
            .in('product_id', productIds)
            .order('position', { ascending: true })
        if (galleryErr) console.error('[productsService.getByStoreId] product_images:', galleryErr.message)
        const firstGalleryUrlByProduct: Record<string, string> = {}
        for (const row of galleryRows || []) {
            const pid = (row as { product_id: string }).product_id
            const url = String((row as { url?: string | null }).url ?? '').trim()
            if (!url || firstGalleryUrlByProduct[pid] !== undefined) continue
            firstGalleryUrlByProduct[pid] = url
        }

        return list.map((p: Product & { id: string }) => {
            const galleryCover = firstGalleryUrlByProduct[p.id]
            const pBase = {
                ...p,
                cover_image: p.cover_image || p.image || galleryCover || null,
                image: p.image || p.cover_image || galleryCover || null,
            }
            const a = agg[pBase.id]
            const sw = swatchesByProduct[pBase.id]
            if (!pBase.has_variants || !a) {
                const row = sw?.length ? { ...pBase, variant_swatches: sw } : pBase
                return stripInternalProductShippingFields(row as Record<string, unknown>) as Product
            }
            // Dropshipping products don't track local stock — never downgrade their status
            const isDs = isDropshippingProduct(pBase)
            const derivedStatus = (a.total_stock === 0 && !isDs) ? 'out_of_stock' : pBase.status
            return stripInternalProductShippingFields({
                ...pBase,
                is_dropshipping: isDs,
                variant_count: a.count,
                min_price: a.min_price === Infinity ? undefined : a.min_price,
                max_price: a.max_price === -Infinity ? undefined : a.max_price,
                total_stock: a.total_stock,
                price: a.min_price === Infinity ? pBase.price : a.min_price,
                stock: a.total_stock,
                status: derivedStatus,
                ...(sw?.length ? { variant_swatches: sw } : {}),
            } as Record<string, unknown>) as Product
        })
    },

    /**
     * Storefront search (public): server-side filtering on products table.
     * - status = active
     * - store scoped
     * - case-insensitive match on name OR meta_description
     *
     * NOTE: pricing is display-only; checkout remains authoritative.
     */
    async searchStorefront(storeId: string, query: string, opts?: { limit?: number }): Promise<Product[]> {
        const q = (query || '').trim()
        if (!storeId) return []
        if (!q) return []

        const limit = Math.min(60, Math.max(1, Number(opts?.limit ?? 24)))
        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId, q })
            params.set('limit', String(limit))
            const j = await appFetchPublic<{ ok: true; rows: Product[] }>(
                `/api/app/public/catalog/search?${params.toString()}`
            )
            if (!j || typeof j !== 'object' || !Array.isArray((j as { rows?: unknown }).rows)) {
                throw new Error('Invalid API response')
            }
            return (j as { rows: Product[] }).rows
        }

        const orSearch = buildOrIlikeClause(['name', 'meta_description'], q)

        let qb = db
            .from('products')
            .select('*, category:categories(id, slug, name)')
            .eq('store_id', storeId)
            .or(STOREFRONT_VISIBLE_PRODUCT_FILTER)
        if (orSearch) qb = qb.or(orSearch)
        const { data: products, error } = await qb.order('created_at', { ascending: false }).limit(limit)

        if (error) throw error
        return (products || []).map((p) =>
            stripInternalProductShippingFields(p as Record<string, unknown>) as Product,
        )
    },

    // Get products filtered by category slug (used by CategoryProductsPage)
    async getProductsByCategory(storeId: string, categorySlug: string): Promise<Product[]> {
        if (!storeId || !categorySlug) return []

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId, categorySlug })
            const j = await appFetchPublic<{ ok: true; rows: Product[] }>(
                `/api/app/public/catalog/products?${params.toString()}`
            )
            if (!j || typeof j !== 'object' || !Array.isArray((j as { rows?: unknown }).rows)) {
                throw new Error('Invalid API response')
            }
            return (j as { rows: Product[] }).rows
        }

        // Two-step: resolve slug → id, then fetch products
        const { data: cat, error: catError } = await db
            .from('categories')
            .select('id')
            .eq('store_id', storeId)
            .eq('slug', categorySlug)
            .eq('is_active', true)
            .single()

        if (catError || !cat) return []

        return productsService.getByStoreId(storeId, { categoryId: cat.id })
    },

    // Get single product by ID with variants, options, product_images
    // `storeId` is optional but helps avoid "not found" caused by store-scoping / RLS.
    async getById(id: string, storeId?: string): Promise<Product | null> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const qs = storeId
                ? `?storeId=${encodeURIComponent(storeId)}`
                : ''
            const j = await appFetch<{ ok: true; product: Product | null }>(
                `/api/app/products/${encodeURIComponent(id)}${qs}`
            )
            if (!j || typeof j !== 'object' || !('product' in j)) {
                throw new Error('Invalid API response')
            }
            return (j as { product: Product | null }).product
        }

        let q = db
            .from('products')
            .select(`
              *,
              variants:product_variants!product_variants_product_id_fkey(*)
            `)
            .eq('id', id)
        if (storeId) {
            q = q.eq('store_id', storeId)
        }
        const { data: product, error } = await q.single()

        if (error) {
            if (error.code === 'PGRST116') return null
            throw error
        }
        if (!product) return null

        const [optsRes, imgsRes] = await Promise.all([
            db.from('product_options').select('*, values:product_option_values(*)').eq('product_id', id).order('position'),
            db.from('product_images').select('*').eq('product_id', id).order('position')
        ])
        const options = (optsRes.data || []).map((o: any) => ({ ...o, values: (o.values || []).sort((a: any, b: any) => a.position - b.position) }))
        const product_images = imgsRes.data || []

        const rawVariants = (product.variants || []) as any[]
        const missingImgCount = rawVariants.filter((v) => !v.image_url).length
        if (missingImgCount > 0) console.warn(`[products] ${missingImgCount} variant(s) missing image_url on product ${id}`)
        const variants = rawVariants.map((v: any) => ({
            ...v,
            attributes: normalizeVariantAttributesForWrite((v.attributes || {}) as Record<string, string>),
            image: v.image_url || null,
        }))

        const optionsOut =
            product.has_variants && variants.length > 0
                ? detailOptionsMergedFromVariants(product.id, variants, options)
                : options.sort((a: any, b: any) => a.position - b.position)

        return {
            ...product,
            options: optionsOut,
            product_images,
            variants
        } as Product
    },

    // Create new product. (variants?, options?) — backward compat: create(storeId, product, variants) still works.
    async create(
        storeId: string,
        product: Omit<ProductInsert, 'store_id'>,
        variants?: Omit<ProductVariant, 'id' | 'product_id' | 'user_id' | 'store_id'>[],
        options?: { name: string; values: string[] }[]
    ): Promise<Product> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; product: Product }>('/api/app/products', {
                method: 'POST',
                json: { storeId, product, variants, options },
            })
            if (!j?.product) throw new Error('Invalid API response')
            return j.product
        }

        const hasVariants = Boolean(variants && variants.length > 0)
        const explicitProductType = (product as any).product_type as ProductType | undefined
        if (explicitProductType === 'variable' && !hasVariants) {
            throw new Error(
                'Invalid product payload: product_type is variable but no variant rows were provided.',
            )
        }
        /** Rows win over label: variable imports must always set has_variants + product_type together. */
        const productType: ProductType = hasVariants
            ? 'variable'
            : (explicitProductType ?? 'simple')
        const slug = await ensureUniqueProductSlug(user.id, (product as any).slug ?? product.name, undefined)

        const { data: newProduct, error } = await db
            .from('products')
            .insert({
                ...product,
                slug,
                user_id: user.id,
                store_id: storeId,
                has_variants: hasVariants,
                product_type: productType
            })
            .select()
            .single()

        if (error) throw error

        const safeOptions = (options ?? []).slice(0, 3)
        if ((options ?? []).length > 3) {
            // eslint-disable-next-line no-console -- intentional: surface silent truncation in devtools
            console.warn('[products.create] product_options limited to 3 axes; extra dimensions omitted', {
                received: options?.length,
                kept: safeOptions.length,
            })
        }

        if (hasVariants && variants && variants.length > 0) {
            const normalizedPreview = variants.map((v, pidx) => {
                const n = resolveVariantAttributesForPersist({
                    attributes: (v as any).attributes,
                    name: (v as any).name,
                    sku: (v as any).sku,
                    external_sku_id: (v as any).external_sku_id ?? null,
                })
                assertVariantHasPublicAttributes(n, `Create product (options derive) variant #${pidx + 1}`)
                return n
            })
            const derivedOpts = deriveProductOptionsFromVariantRows(
                normalizedPreview.map((attributes) => ({ attributes })),
            )
            await replaceProductOptionsFromDerived(newProduct.id, derivedOpts)
        } else if (safeOptions.length > 0) {
            for (let i = 0; i < safeOptions.length; i++) {
                const opt = safeOptions[i]
                const { data: optRow, error: optErr } = await db
                    .from('product_options')
                    .insert({ product_id: newProduct.id, name: opt.name, position: i })
                    .select('id')
                    .single()
                if (optErr) throw optErr
                if (optRow && opt.values.length > 0) {
                    await db.from('product_option_values').insert(
                        opt.values.map((value, j) => ({ option_id: optRow.id, value, position: j })),
                    )
                }
            }
        }

        if (variants && variants.length > 0) {
            if (variants.length > 100) throw new Error('Max 100 variants per product')
            const slugBase = (product.slug || newProduct.name || 'product').toString().replace(/\s+/g, '-').toUpperCase().slice(0, 20)
            const usedSkus = new Set<string>()
            const rows = variants.map((v, idx) => {
                const normalized = resolveVariantAttributesForPersist({
                    attributes: (v as any).attributes,
                    name: (v as any).name,
                    sku: (v as any).sku,
                    external_sku_id: (v as any).external_sku_id ?? null,
                })
                assertVariantHasPublicAttributes(normalized, `Create product variant #${idx + 1}`)
                if (Object.keys(normalized).length === 0) {
                    console.warn('⚠️ Variant missing attributes', {
                        name: (v as any).name,
                        sku: (v as any).sku,
                        product_id: newProduct.id,
                    })
                }
                let sku = (v as any).sku || v.sku
                if (!sku && Object.keys(normalized).length > 0) {
                    const parts = [slugBase, ...Object.values(normalized)].join('-').replace(/\s+/g, '-')
                    sku = parts
                    let n = 1
                    while (usedSkus.has(sku)) {
                        sku = `${parts}-${n}`
                        n++
                    }
                    usedSkus.add(sku)
                } else if (sku) {
                    const baseSku = String(sku)
                    let candidate = baseSku
                    let n = 1
                    while (usedSkus.has(candidate)) {
                        candidate = `${baseSku}-${n}`
                        n++
                    }
                    usedSkus.add(candidate)
                    sku = candidate
                }
                const sq = coerceVariantStockFields(v)
                return {
                    product_id: newProduct.id,
                    store_id: storeId,
                    user_id: user.id,
                    name: (v as any).name ?? Object.values(normalized).join(' / '),
                    sku: sku || null,
                    price: (v as any).price ?? v.price,
                    ...sq,
                    image_url: resolveVariantImageUrl(v),
                    position: (v as any).position ?? idx,
                    status: (v as any).status ?? 'active',
                    attributes: normalized,
                    barcode: (v as any).barcode ?? null,
                    compare_at_price: (v as any).compare_at_price ?? null,
                    weight: (v as any).weight ?? null,
                    cost_price: (v as any).cost_price ?? null,
                    is_default: idx === 0,
                    inventory_policy: (v as any).inventory_policy ?? 'deny',
                    inventory_reserved: (v as any).inventory_reserved ?? 0,
                    supplier_unit_price: (v as any).supplier_unit_price ?? null,
                    supplier_currency: (v as any).supplier_currency ?? null,
                }
            })
            const { error: varError } = await db.from('product_variants').insert(rows)
            if (varError) throw varError

            // Set default_variant_id to the first variant (is_default = true)
            const { data: firstVar } = await db
                .from('product_variants')
                .select('id')
                .eq('product_id', newProduct.id)
                .eq('is_default', true)
                .limit(1)
                .single()
            if (firstVar) {
                await db.from('products').update({ default_variant_id: firstVar.id }).eq('id', newProduct.id)
            }
        } else {
            // ─── DEFAULT VARIANT GUARANTEE ────────────────────────────────────────
            // Every product MUST have at least one variant so orders never crash.
            // For dropshipping products: keep inventory fields neutral (no stock semantics).
            const isDs = Boolean((product as any).is_dropshipping)
            const sellingPrice = Number((product as any).price) || 0
            const baseStock = isDs ? 0 : Math.max(0, Math.floor(Number((product as any).stock ?? 0) || 0))
            const productCompareAt = (product as any).compare_at_price ?? null
            const defaultVariantRow = {
                product_id: newProduct.id,
                store_id: storeId,
                user_id: user.id,
                name: 'Default',
                sku: null,
                price: sellingPrice,
                stock: baseStock,
                image_url: null,
                position: 0,
                status: 'active' as const,
                attributes: {},
                barcode: null,
                compare_at_price: productCompareAt,
                weight: null,
                cost_price: (product as any).purchase_price ?? (product as any).supplier_price ?? null,
                is_default: true,
                inventory_policy: isDs ? 'continue' : 'deny',
                inventory_quantity: isDs ? null : baseStock,
                inventory_reserved: 0,
            }
            const { data: defaultVar, error: defaultVarError } = await db
                .from('product_variants')
                .insert(defaultVariantRow)
                .select('id')
                .single()
            if (defaultVarError) {
                console.error('[DEFAULT VARIANT] Failed to create default variant:', defaultVarError)
            } else if (defaultVar) {
                await db.from('products').update({ default_variant_id: defaultVar.id }).eq('id', newProduct.id)
            }
        }

        return newProduct
    },

    async update(
        id: string,
        updates: ProductUpdate,
        variants?: Omit<ProductVariant, 'id' | 'product_id' | 'user_id' | 'store_id'>[],
        options?: { name: string; values: string[] }[]
    ): Promise<Product> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; product: Product }>(`/api/app/products/${encodeURIComponent(id)}`, {
                method: 'PUT',
                json: { updates, variants, options },
            })
            if (!j?.product) throw new Error('Invalid API response')
            return j.product
        }

        const productData = await db.from('products').select('store_id').eq('id', id).single()
        const storeId = productData.data?.store_id

        const updatePayload = { ...updates, ...(variants !== undefined && { has_variants: variants && variants.length > 0 }) }
        if (updatePayload.slug !== undefined) {
            updatePayload.slug = await ensureUniqueProductSlug(user.id, updatePayload.slug, id)
        }

        const { data, error } = await db
            .from('products')
            .update(updatePayload)
            .eq('id', id)
            .select()
            .single()

        if (error) throw error

        // Simple products (no variant payload in this update): keep default variant COGS aligned with products.purchase_price
        if (variants === undefined && 'purchase_price' in updates) {
            const { data: prodRow } = await db
                .from('products')
                .select('default_variant_id')
                .eq('id', id)
                .single()
            const dv = prodRow?.default_variant_id
            if (dv) {
                const pp = updates.purchase_price
                await db
                    .from('product_variants')
                    .update({ cost_price: pp == null || Number.isNaN(Number(pp)) ? null : Number(pp) })
                    .eq('id', dv)
            }
        }

        if (options !== undefined && variants === undefined) {
            const { data: existingOpts } = await db.from('product_options').select('id').eq('product_id', id)
            if (existingOpts?.length) {
                await db.from('product_option_values').delete().in('option_id', existingOpts.map((o: any) => o.id))
                await db.from('product_options').delete().eq('product_id', id)
            }
            const safeOptsUpdate = options.slice(0, 3)
            if (options.length > 3) {
                // eslint-disable-next-line no-console
                console.warn('[products.update] product_options limited to 3 axes', { received: options.length })
            }
            if (safeOptsUpdate.length > 0) {
                for (let i = 0; i < safeOptsUpdate.length; i++) {
                    const opt = safeOptsUpdate[i]
                    const { data: optRow, error: optErr } = await db
                        .from('product_options')
                        .insert({ product_id: id, name: opt.name, position: i })
                        .select('id')
                        .single()
                    if (optErr) throw optErr
                    if (optRow && opt.values.length > 0) {
                        await db.from('product_option_values').insert(
                            opt.values.map((value, j) => ({ option_id: optRow.id, value, position: j }))
                        )
                    }
                }
            }
        }

        if (variants !== undefined && storeId) {
            if (variants.length > 100) throw new Error('Max 100 variants per product')
            const normalizedList = variants.map((v) =>
                resolveVariantAttributesForPersist({
                    attributes: (v as any).attributes,
                    name: (v as any).name,
                    sku: (v as any).sku,
                    external_sku_id: (v as any).external_sku_id ?? null,
                }),
            )
            normalizedList.forEach((normalized, idx) => {
                assertVariantHasPublicAttributes(normalized, `Update product ${id} variant #${idx + 1}`)
            })
            const nextValueSet = new Set<string>()
            for (const na of normalizedList) {
                for (const [k, val] of Object.entries(na)) {
                    if (k.startsWith('_')) continue
                    if (val) nextValueSet.add(String(val).trim())
                }
            }
            const { data: existingVariants } = await db
                .from('product_variants')
                .select('*')
                .eq('product_id', id)
            const existingList = (existingVariants || []) as ProductVariant[]

            for (const v of existingList) {
                const attrs = normalizeVariantAttributesForWrite((v.attributes || {}) as Record<string, string>)
                const hasRemovedValue = Object.values(attrs).some((val) => !nextValueSet.has(String(val).trim()))
                if (hasRemovedValue && v.status !== 'archived') {
                    await db
                        .from('product_variants')
                        .update({ status: 'archived', deleted_at: new Date().toISOString() })
                        .eq('id', v.id)
                }
            }

            const slugBase = (data.slug || data.name || 'product').toString().replace(/\s+/g, '-').toUpperCase().slice(0, 20)
            const usedSkus = new Set<string>()
            const existingByKey = new Map<string, ProductVariant>()
            existingList.forEach((ev: ProductVariant) => {
                const key = JSON.stringify(
                    normalizeVariantAttributesForWrite((ev.attributes || {}) as Record<string, string>),
                )
                existingByKey.set(key, ev)
            })

            for (let idx = 0; idx < variants.length; idx++) {
                const v = variants[idx]
                const normalized = normalizedList[idx]
                const key = JSON.stringify(normalized)
                const existing = existingByKey.get(key)
                let sku = (v as any).sku ?? v.sku
                if (!sku && Object.keys(normalized).length > 0) {
                    const parts = [slugBase, ...Object.values(normalized)].join('-').replace(/\s+/g, '-')
                    sku = parts
                    let n = 1
                    while (usedSkus.has(sku)) {
                        sku = `${parts}-${n}`
                        n++
                    }
                    usedSkus.add(sku)
                } else if (sku) {
                    const baseSku = String(sku)
                    let candidate = baseSku
                    let n = 1
                    while (usedSkus.has(candidate)) {
                        candidate = `${baseSku}-${n}`
                        n++
                    }
                    usedSkus.add(candidate)
                    sku = candidate
                }
                const row = {
                    store_id: storeId,
                    name: (v as any).name ?? Object.values(normalized).join(' / '),
                    sku: sku || null,
                    price: (v as any).price ?? v.price,
                    ...coerceVariantStockFields(v),
                    image_url: resolveVariantImageUrl(v),
                    position: (v as any).position ?? idx,
                    status: (v as any).status ?? 'active',
                    attributes: normalized,
                    barcode: (v as any).barcode ?? null,
                    compare_at_price: (v as any).compare_at_price ?? null,
                    weight: (v as any).weight ?? null,
                    cost_price: (v as any).cost_price ?? null,
                    is_default: idx === 0,
                    inventory_policy: (v as any).inventory_policy ?? 'deny',
                    inventory_reserved: (v as any).inventory_reserved ?? 0,
                    supplier_unit_price: (v as any).supplier_unit_price ?? null,
                    supplier_currency: (v as any).supplier_currency ?? null,
                }
                if (existing) {
                    await db.from('product_variants').update(row).eq('id', existing.id)
                } else {
                    await db.from('product_variants').insert({
                        ...row,
                        product_id: id,
                        user_id: user.id
                    })
                }
            }

            const derivedOpts = deriveProductOptionsFromVariantRows(
                normalizedList.map((attributes) => ({ attributes })),
            )
            await replaceProductOptionsFromDerived(id, derivedOpts)
        }

        return data
    },

    /** Storefront: full payload with default_variant, options, variants, variant_lookup (key format "Color:Red|Size:M"). */
    async getProductForStorefront(productIdOrSlug: string, storeId: string): Promise<ProductStorefrontPayload | null> {
        if (isAppApiConfigured()) {
            const isUuidKey = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productIdOrSlug)
            const params = new URLSearchParams({ storeId })
            if (isUuidKey) params.set('id', productIdOrSlug)
            else params.set('slug', productIdOrSlug)
            const j = await appFetchPublic<{ ok: true; payload: ProductStorefrontPayload }>(
                `/api/app/public/catalog/product-payload?${params.toString()}`
            )
            if (!j || typeof j !== 'object' || !('payload' in j)) {
                throw new Error('Invalid API response')
            }
            return (j as { payload: ProductStorefrontPayload }).payload ?? null
        }

        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productIdOrSlug)
        // Include dropshipping products even if status = 'out_of_stock'
        let query = db
            .from('products')
            .select('*')
            .eq('store_id', storeId)
            .or(STOREFRONT_VISIBLE_PRODUCT_FILTER)
        if (isUuid) query = query.eq('id', productIdOrSlug)
        else query = query.eq('slug', productIdOrSlug)
        const { data: product, error } = await query.single()
        if (error || !product) return null

        const [productImagesRes, variantsRes] = await Promise.all([
            db.from('product_images').select('*').eq('product_id', product.id).order('position'),
            // Include 'out_of_stock' variants so the PDP can resolve the default variant
            // and show its price. Archived/inactive variants are excluded — they're
            // genuinely hidden. The canAdd guard in ProductDetailsBlock prevents adding
            // out_of_stock variants to cart.
            db.from('product_variants').select('*').eq('product_id', product.id).or('status.eq.active,status.eq.out_of_stock').order('position')
        ])
        const productImages = (productImagesRes.data || []) as ProductImage[]
        const variants = (variantsRes.data || []) as ProductVariant[]

        const missingImgCount2 = variants.filter((v) => !v.image_url).length
        if (missingImgCount2 > 0) console.warn(`[products] ${missingImgCount2} variant(s) missing image_url on product ${product.id}`)
        const variantsWithImages = variants.map((v) => ({
            ...v,
            attributes: normalizeVariantAttributesForWrite((v.attributes || {}) as Record<string, string>),
            image: v.image_url || null,
        }))

        const options = deriveProductOptionsFromVariantRows(variantsWithImages).map((o) => ({
            name: o.name,
            values: o.values,
        }))

        // ─── SAFE DEFAULT VARIANT ─────────────────────────────────────────────
        // Priority: is_default=true → product.default_variant_id → first active variant
        // NEVER returns null if at least one variant exists — prevents cart crashes.
        const defaultVariant =
            variantsWithImages.find(v => v.is_default) ||
            variantsWithImages.find(v => v.id === product.default_variant_id) ||
            variantsWithImages[0]

        const variant_lookup: Record<string, string> = {}
        variantsWithImages.forEach(v => {
            const attrs = (v.attributes || {}) as Record<string, string>
            const publicAttrs = stripInternalAttributeKeys(attrs)
            if (Object.keys(publicAttrs).length > 0) {
                const lk = variantLookupKey(publicAttrs)
                if (variant_lookup[lk] === undefined) variant_lookup[lk] = v.id
            }
        })

        return {
            product: stripInternalProductShippingFields({
                ...product,
                cover_image: product.cover_image || product.image,
                // Use product_images table when it has rows (new gallery system).
                // When empty, keep the original products.images string[] (legacy format)
                // so collectImages() can still build the gallery from it.
                // Overwriting with [] here was silently destroying all legacy gallery images.
                ...(productImages.length > 0
                    ? { images: productImages.map(pi => ({ url: pi.url, position: pi.position })) }
                    : {}),
            } as Record<string, unknown>) as Product & { images?: { url: string; position: number }[] },
            default_variant: defaultVariant?.id ?? null,
            options,
            variants: variantsWithImages,
            variant_lookup
        }
    },

    /**
     * Flatten `getProductForStorefront` into list/grid `Product` (variants + gallery).
     * Use on the **public** vitrine — unlike `getById`, does not require an authenticated seller.
     */
    mergeStorefrontPayloadToProduct(payload: ProductStorefrontPayload): Product {
        const base = payload.product
        const rawImages = (base as Product & { images?: unknown }).images
        const imgObjects =
            Array.isArray(rawImages) &&
            rawImages.length > 0 &&
            typeof rawImages[0] === 'object' &&
            rawImages[0] !== null &&
            'url' in (rawImages[0] as object)
                ? (rawImages as { url: string; position?: number }[])
                : []
        const stringImages =
            Array.isArray(rawImages) && rawImages.length > 0 && typeof rawImages[0] === 'string'
                ? (rawImages as string[])
                : []

        const product_images: ProductImage[] = imgObjects.map((row, i) => ({
            id: `sf-${base.id}-${i}`,
            product_id: base.id,
            url: row.url,
            position: row.position ?? i,
        }))

        const galleryUrls =
            imgObjects.length > 0 ? imgObjects.map((r) => r.url).filter(Boolean) : stringImages

        return stripInternalProductShippingFields({
            ...base,
            variants: payload.variants,
            options: payload.options,
            ...(product_images.length ? { product_images } : {}),
            images: galleryUrls,
            cover_image: base.cover_image || base.image || galleryUrls[0] || null,
            image: base.image || base.cover_image || galleryUrls[0] || null,
        } as Record<string, unknown>) as Product
    },

    async delete(id: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            await appFetch<{ ok: true }>(`/api/app/products/${encodeURIComponent(id)}`, { method: 'DELETE' })
            return
        }

        const { error } = await db
            .from('products')
            .delete()
            .eq('id', id)
        // RLS handles ownership via store_id

        if (error) throw error
    },

    async deleteMany(ids: string[]): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            await appFetch<{ ok: true }>('/api/app/products/bulk-delete', {
                method: 'POST',
                json: { ids },
            })
            return
        }

        const { error } = await db
            .from('products')
            .delete()
            .in('id', ids)
        // RLS handles ownership via store_id

        if (error) throw error
    },

    async search(query: string, storeId: string): Promise<Product[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId, q: query })
            const j = await appFetch<{ ok: true; rows: Product[] }>(
                `/api/app/products/search?${params.toString()}`
            )
            if (!j || typeof j !== 'object' || !Array.isArray((j as { rows?: unknown }).rows)) {
                throw new Error('Invalid API response')
            }
            return (j as { rows: Product[] }).rows
        }

        const { data: products, error } = await db
            .from('products')
            .select('*')
            .eq('store_id', storeId)
            .ilike('name', `%${query}%`)
            .order('created_at', { ascending: false })

        if (error) throw error
        const list = products || []
        if (list.length === 0) return []

        const productIds = list.map((p: { id: string }) => p.id)
        const { data: variants } = await db
            .from('product_variants')
            .select('product_id, price, inventory_quantity')
            .in('product_id', productIds)
            .eq('status', 'active')

        const agg: Record<string, { min_price: number; max_price: number; count: number; total_stock: number }> = {}
        for (const v of variants || []) {
            const pid = (v as { product_id: string }).product_id
            if (!agg[pid]) agg[pid] = { min_price: Infinity, max_price: -Infinity, count: 0, total_stock: 0 }
            const p = (v as { price: number | null; inventory_quantity: number | null }).price
            const q = (v as { price: number | null; inventory_quantity: number | null }).inventory_quantity ?? 0
            if (p != null) {
                agg[pid].min_price = Math.min(agg[pid].min_price, p)
                agg[pid].max_price = Math.max(agg[pid].max_price, p)
            }
            agg[pid].count += 1
            agg[pid].total_stock += Number(q) || 0
        }

        return list.map((p: Product & { id: string }) => {
            const a = agg[p.id]
            if (!p.has_variants || !a) return p
            // Dropshipping products do not use local inventory, so a zero
            // variant stock aggregate must not hide them as out of stock.
            const isDs = isDropshippingProduct(p)
            const derivedStatus = (a.total_stock === 0 && !isDs) ? 'out_of_stock' : p.status
            return {
                ...p,
                is_dropshipping: isDs,
                variant_count: a.count,
                min_price: a.min_price === Infinity ? undefined : a.min_price,
                max_price: a.max_price === -Infinity ? undefined : a.max_price,
                total_stock: a.total_stock,
                price: a.min_price === Infinity ? p.price : a.min_price,
                stock: a.total_stock,
                status: derivedStatus,
            }
        })
    },

    async updateStock(id: string, stock: number): Promise<Product> {
        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; product: Product }>(
                `/api/app/products/${encodeURIComponent(id)}/stock`,
                { method: 'PATCH', json: { stock } }
            )
            if (!j?.product) throw new Error('Invalid API response')
            return j.product
        }
        // ─── STOCK GUARD: never mutate inventory for dropshipping products ────
        const { data: p } = await db.from('products').select('is_dropshipping').eq('id', id).single()
        if (guardStockForNormalOnly(p)) {
            return this.getById(id) as Promise<Product>
        }
        const status = stock === 0 ? 'out_of_stock' : 'active'
        return this.update(id, { stock, status })
    }
}
